import Lock from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UpdateIcon from '@mui/icons-material/Update';
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import {styled} from '@mui/material';
import {i18n} from 'i18next';
import moment from 'moment';
import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Column} from 'react-table';

import {WarningAmberOutlined} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {
  Dialog,
  Table,
  TableCellMenu,
  TablePagination,
  TableSort,
  ToastSeverity,
  useLoader,
  useToast
} from 'thermia-react-container';
import DeviceIcon from '../../../../components/assets/icons/deviceIcon';
import {RegistrationsContext} from '../../../../context/contextProvider';
import {useRegistrationService} from '../../../../service/registrationService';
import {Registration, RegistrationMetaData} from '../../../../types/registrationTypes';
import {
  extractDevicesArticleNameFromMetaData,
  extractDeviceSerialNumberFromMetaData
} from '../../../../validation/extractDeviceSerialNumber';
import ReopenAdminForm from './reopenAdminForm';
import {OnBehalfOfService} from "../../../../service/onBehalfOfService";
import {DeviceMetaData} from "../../../../types/device";
import {useRefreshData} from "../../../registration/hooks/useRefreshData";
import InsuranceInfo from "./insuranceInfo";

const Status = {
  INVALID_DEVICE: 'INVALID_DEVICE',
  UNREGISTERED_DEVICE: 'UNREGISTERED_DEVICE',
  COMPLETED: 'COMPLETED',
  HIDDEN: 'HIDDEN',
  LOCKED: 'LOCKED',
  CREATED: 'CREATED',
  CHECK: 'CHECK',
}

type CompletedRegistrationsProps = {
  adminRegistrations: Registration[];
  updateList: (id: string) => Promise<void>;
};

export interface AdministrationCompletedForTable {
  id: string;
  deviceSerialNumbers: string;
  deviceArticleName: string;
  deliverDate: (string | undefined)[];
  updatedAt: string;
  customerId: string[];
  partnerId: string;
  installationDate: string;
  status: string;
  hasInsurance: boolean;
  hasReturnedItems: { customerId: string }[];
  evaluatedStatus: string;
  devices: DeviceMetaData[];
}

const mapAdminRegistrations = (adminRegistrations: RegistrationMetaData[], t: i18n['t']): AdministrationCompletedForTable[] => {
  if (!Array.isArray(adminRegistrations)) {
    console.error('adminRegistrations is not an array');
    return [];
  }
  return adminRegistrations.map((reg) => ({
    id: reg.id,
    updatedAt: reg.updated ?? '',
    installationDate: reg.installationDate ? moment(reg.installationDate).format('YYYY-MM-DD') : '',
    customerId: loopThroughCustomerIds(reg) ?? '',
    deviceSerialNumbers: extractDeviceSerialNumberFromMetaData(reg),
    partnerId: reg.partnerId ?? '',
    deviceArticleName: extractDevicesArticleNameFromMetaData(reg, t, true),
    hasReturnedItems: reg.status === Status.UNREGISTERED_DEVICE ? [] :returnedCheck(reg),
    deliverDate: reg.devices?.items?.map((item) => item.deliverDate) ?? [],
    status: reg.status ?? '',
    hasInsurance: reg.devices?.items.some(d => d.insurance) === true,
    evaluatedStatus: evaluateStatus(reg),
    devices: reg.devices?.items ?? [],
  }));
};

const returnedCheck = (registration: RegistrationMetaData) => {
  const partnerId = registration.partnerId;
  const items = registration.devices?.items;
  if (items) {
    const mismatchArray = items.filter((item) => item.partnerId !== partnerId);
    return mismatchArray;
  }
  return [];
};

const loopThroughCustomerIds = (valueArray: RegistrationMetaData) => {
  const customerIds: string[] = [];
  valueArray.devices?.items?.forEach((device) => {
    if (!device.customerId) {
      customerIds.push('-');
    } else {
      customerIds.push(device.customerId);
    }
  });
  const ifAllCustomerIdsAreEqual = customerIds.every((val, i, arr) => val === arr[0]);
  if (ifAllCustomerIdsAreEqual) {
    return [customerIds[0]];
  }
  return customerIds;
};

const evaluateStatus = (reg: RegistrationMetaData) => {
  if (reg.status === Status.UNREGISTERED_DEVICE) {
    if (reg.devices?.items?.[0].allowRegistration) {
      return Status.UNREGISTERED_DEVICE;
    } else {
      return Status.HIDDEN;
    }
  } else {
    return reg.status ?? '';
  }
};

const StyledContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '36px',
  nowrap: 'nowrap'
}));

const StyledStatusWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  flexDirection: 'column'
}));

const StyledStatusContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  height: '36px',
  flexDirection: 'row'
}));

const AdminRegistrationsTable: FC<CompletedRegistrationsProps> = ({
  adminRegistrations,
  updateList
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowMenuOpenId, setRowMenuOpenId] = useState<string>();
  const { getRegistration, getPartner } = useRegistrationService(true);
  const { registrations } = useContext(RegistrationsContext);
  const { setActiveRegistration } = registrations;
  const refreshData = useRefreshData();
  const { Loader } = useLoader();
  const { addToast } = useToast();
  const { t } = useTranslation('registrations');

  const [selectedRegistration, setSelectedRegistration] = useState<AdministrationCompletedForTable>();
  const [insuranceDetails, setInsuranceDetails] = useState<AdministrationCompletedForTable>();

  const setOnBehalfOf = useCallback(async (row: AdministrationCompletedForTable) => {
    OnBehalfOfService.reset();
    if(row.partnerId) {
      setLoading(true);
      OnBehalfOfService.partner = await getPartner(row.partnerId).catch(() => null);
      refreshData();
    }
    if(!OnBehalfOfService.partner) {
      addToast(t('registrations.admin.actingOnBehalfOfError', { customerId: row.customerId, partnerId: row.partnerId }), ToastSeverity.ERROR);
    }
    setLoading(false);
    return !!OnBehalfOfService.partner;
  }, [addToast, getPartner, refreshData, t]);

  const navigate = useNavigate();
  const calculateLocked = useCallback(
    (id: string) => {
      const currentReg = adminRegistrations.find((reg) => reg.id === id);

      const isRegistrationEditable = (updatedAt: string | undefined): boolean => {
        const days: number = 30;
        if (updatedAt === undefined) {
          return false;
        }
        const daysAgo = new Date().getTime() - days * 24 * 60 * 60 * 1000;
        const updatedAtInMs = new Date(updatedAt).getTime();
        return daysAgo <= updatedAtInMs;
      };

      const registrationIsEditable = isRegistrationEditable(currentReg?.updated);
      if (currentReg?.status === Status.COMPLETED && !registrationIsEditable) {
        return true;
      }
      return false;
    },
    [adminRegistrations]
  );

  const flattenedAdminRegistrations = useMemo(() => {
    return mapAdminRegistrations(adminRegistrations, t);
  }, [adminRegistrations, t]);

  const fetchDataFunction = (
    pagination: TablePagination | null,
    search: string | null,
    sort: TableSort | null
  ): Promise<{ data: AdministrationCompletedForTable[]; totalCount: number }> => {
    return new Promise((resolve) => {
      const data = flattenedAdminRegistrations;
      const filteredData = data.slice(pagination!.skip, pagination!.skip + pagination!.take);
      const totalCount = data.length;
      resolve({ data: filteredData, totalCount });
    });
  };

  const [tableMethods] = useState({
    refreshData: () => Promise.resolve() // function will be set by the table
  });

  const refreshTableData = useCallback(async () => {
    await tableMethods.refreshData();
  }, [tableMethods]);

  useEffect(() => {
    refreshTableData();
  }, [adminRegistrations, refreshTableData]);

  const selectIconWithStatus = (status: string) => {
    switch (status) {
      case Status.LOCKED:
        return (
          <StyledStatusContainer>
            <Lock color="error" /> {status}
          </StyledStatusContainer>
        );
      case Status.COMPLETED:
        return (
          <StyledStatusContainer>
            <LockOpenIcon color="success" /> {status}
          </StyledStatusContainer>
        );
      case Status.CREATED:
        return (
          <StyledStatusContainer>
            <UpdateIcon /> ONGOING
          </StyledStatusContainer>
        );
      case Status.UNREGISTERED_DEVICE:
        return (
          <StyledStatusContainer>
            <UpdateIcon /> UNREGISTERED
          </StyledStatusContainer>
        );
      case Status.HIDDEN:
        return (
          <StyledStatusContainer>
            <UpdateIcon /> HIDDEN
          </StyledStatusContainer>
        );
      case Status.CHECK:
        return (
          <StyledStatusContainer>
            <CheckCircleOutlinedIcon />
          </StyledStatusContainer>
        );
      default:
        return (
          <StyledStatusContainer>
            <UpdateIcon /> ONGOING
          </StyledStatusContainer>
        );
    }
  };

  const columns: Column<AdministrationCompletedForTable>[] = useMemo(() => {
    return [
      {
        accessor: 'deviceSerialNumbers',
        Header: t('registrations.general.serialNumber') ?? 'SerialNumber',
        sortable: true,
        Cell: (prop) => (
          <>
            {prop.row.original.deviceSerialNumbers.split(',').map((serialNumber, index) => (
              <StyledContainer key={`${prop.row.id}-deviceSerialNumbers-${index}`}>
                <DeviceIcon />
                <span>{serialNumber}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'deviceArticleName',
        Header: t('registrations.general.articleName') ?? 'ArticleName',
        sortable: true,
        Cell: (prop) => (
          <>
            {prop.row.original.deviceArticleName.split(',').map((articleName, index) => (
              <StyledContainer key={`${prop.row.id}-deviceArticleName-${index}`}>
                <span>{articleName}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'deliverDate',
        sortable: true,
        Header: t('registrations.general.deliverDate') ?? 'deliverDate',
        Cell: (prop) => (
          <>
            {prop.row.original.deliverDate?.map((item, index) => {
              return <StyledContainer key={`${prop.row.id}-deliverDate-${index}`}>{item ? moment(item).format('YYYY-MM-DD') : ''}</StyledContainer>;
            })}
          </>
        )
      },
      {
        accessor: 'updatedAt',
        sortable: true,
        Header: t('registrations.general.registered') ?? 'registered',
        Cell: (prop) => <>{prop.row.original.status !== Status.UNREGISTERED_DEVICE && prop.row.original.updatedAt !== '' ? moment(prop.row.original.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}</>
      },
      {
        accessor: 'installationDate',
        sortable: true,
        Header: t('registrations.general.installationDate') ?? 'installationDate',
        Cell: (prop) => <>{prop.row.original.status !== Status.UNREGISTERED_DEVICE && prop.row.original.installationDate !== '' ? moment(prop.row.original.installationDate).format('YYYY-MM-DD') : '-'}</>
      },
      {
        accessor: 'customerId',
        sortable: true,
        Header: t('registrations.admin.customerNumber') ?? 'customerId',
        Cell: (prop) => (
          <>
            {prop.row.original.customerId.map((customerId, index) => {
              const hasConflict = prop.row.original.hasReturnedItems.find((item) => item.customerId === customerId);
              return (
                <StyledContainer key={`${prop.row.id}-customerId-${customerId}-${index}`} style={{ color: hasConflict ? 'red' : 'black' }}>
                  <span>{customerId}</span>
                  {prop.row.original.hasReturnedItems && hasConflict && (
                    <StyledStatusContainer>
                      <WarningAmberOutlined color="warning" /> Partner missing
                    </StyledStatusContainer>
                  )}
                </StyledContainer>
              );
            })}
            {prop.row.original.partnerId === '' ? <span>{t('registrations.admin.noPartner')}</span> : null}
          </>
        )
      },
      {
        accessor: 'hasInsurance',
        sortable: true,
        Header: t('registrations.general.insurance') ?? 'insurance',
        Cell: (prop) => (
        <>
          <StyledStatusWrapper>
              <span>
                {prop.row.original.hasInsurance ? selectIconWithStatus(Status.CHECK) : null}
              </span>
          </StyledStatusWrapper>
        </>
        )
      },
      {
        accessor: 'status',
        sortable: true,
        Header: t('registrations.general.status') ?? 'status',
        Cell: (prop) => (
          <>
            <StyledStatusWrapper>
              <span>
                {calculateLocked(prop.row.original.id) ? selectIconWithStatus(Status.LOCKED) : selectIconWithStatus(prop.row.original.evaluatedStatus)}
              </span>
            </StyledStatusWrapper>
          </>
        )
      },
      {
        accessor: 'id',
        id: 'menu',
        Header: '',
        Cell: (prop) => {
          return (
            <TableCellMenu
              isOpen={prop.row.original.id === rowMenuOpenId}
              callBack={() => setRowMenuOpenId(prop.row.original.id)}
              menuItems={[
                {
                  onClick: async () => {
                    if(!await setOnBehalfOf(prop.row.original)) {
                      return;
                    }
                    if(prop.row.original.status === Status.UNREGISTERED_DEVICE) {
                      // Create registration for this device on behalf of partner
                      // Devices are of the meta-data type, so cast them as device here to make it work with the
                      // registration form, which expects devices of the device type.
                      const deviceIds = prop.row.original.devices.map(d => d.id);
                      navigate(`/new-registration/devices`, { state: { deviceIds }});
                      return;
                    }
                    // Open registration
                    const fetchedRegistration = await getRegistration(prop.row.original.id);
                    setActiveRegistration(fetchedRegistration);
                    prop.row.original.status === Status.COMPLETED
                      ? navigate(`/completed-registrations/${prop.row.original.id}/customer`, { state: { registrationIsCompleted: true } })
                      : navigate(`/registrations/${prop.row.original.id}/customer`, { state: { registrationIsCompleted: true } });
                  },
                  disabled: prop.row.original.status === Status.INVALID_DEVICE || !prop.row.original.partnerId,
                  label: t(prop.row.original.status === Status.UNREGISTERED_DEVICE ? 'registrations.admin.adminCreateRegistration' : 'registrations.general.view')
                },
                {
                  onClick: async () => {
                    if(await setOnBehalfOf(prop.row.original)) {
                      navigate(`/unregistered-devices`);
                    }
                  },
                  disabled: prop.row.original.status === Status.INVALID_DEVICE || !prop.row.original.partnerId,
                  label: t('registrations.admin.actOnBehalfOf')
                },
                {
                  onClick: () => {
                    setInsuranceDetails(prop.row.original);
                  },
                  disabled: !prop.row.original.hasInsurance,
                  label: t('registrations.admin.insuranceInfo')
                },
                {
                  onClick: () => {
                    setSelectedRegistration(prop.row.original);
                    setOpen(true);
                  },
                  disabled:
                    !prop.row.original.status.includes(Status.COMPLETED) ||
                    calculateLocked(prop.row.original.status) ||
                    prop.row.original.status === Status.UNREGISTERED_DEVICE,
                  label: t('registrations.general.unlock')
                }
              ]}
            />
          );
        }
      }
    ];
  }, [calculateLocked, getRegistration, navigate, rowMenuOpenId, setActiveRegistration, t, setOnBehalfOf]);

  return (
    <>
      <Dialog id={'reopen-completed-admin-registration'} open={open}>
        <ReopenAdminForm updateList={updateList} selectedRegistration={selectedRegistration} handleCancel={() => setOpen(false)} />
      </Dialog>
      <Dialog id={'insurance-info'} open={!!insuranceDetails}>
        <InsuranceInfo registration={insuranceDetails} onClose={() => setInsuranceDetails(undefined)} />
      </Dialog>
      {loading ? (
        <Loader />
      ) : (
        <Table columns={columns} fetchData={fetchDataFunction} exposedMethods={tableMethods} pagination={true} sortable={true} />
      )}
    </>
  );
};

export default AdminRegistrationsTable;
